import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Users, Search } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { nlSubscribers, nlSubscriberStats } from '../lib/marketingApi';

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' }); } catch (e) { return iso; }
}

export default function SubscribersList() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const catMap = (stats?.categories || []).reduce((a, c) => ({ ...a, [c.slug]: c.label }), {});

  useEffect(() => { nlSubscriberStats().then(setStats).catch(() => setStats(null)); }, []);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const d = await nlSubscribers({ status: status || undefined, category: category || undefined, source: source || undefined, search: search || undefined, limit: 200 });
      setItems(d.items || []); setTotal(d.total || 0);
    } catch (e) { toast.error('Failed to load subscribers'); setItems([]); }
  }, [status, category, source, search]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const selBase = 'h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-700 outline-none focus:border-[#0035A8]';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subscribers-list">
      <AdminHeader active="content" />
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><Users className="h-4.5 w-4.5" /></span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Newsletter Subscribers</h1>
            <p className="text-sm text-slate-500">Contacts captured from the site, tracked by source and subscription status</p>
          </div>
        </div>

        {stats && (
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[['Total', stats.total, 'text-slate-900'], ['Subscribed', stats.subscribed, 'text-emerald-600'], ['Unsubscribed', stats.unsubscribed, 'text-rose-600']].map(([l, v, c]) => (
              <div key={l} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`sub-stat-${l.toLowerCase()}`}>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{l}</div>
                <div className={`mt-1 text-2xl font-bold ${c}`}>{(v ?? 0).toLocaleString()}</div>
              </div>
            ))}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Top sources</div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(stats.by_source || []).slice(0, 4).map((s) => (
                  <span key={s.source} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{s.source} · {s.count}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email / name / company" data-testid="sub-search"
              className="h-9 w-64 rounded-md border border-slate-300 bg-white pl-8 pr-3 text-sm outline-none focus:border-[#0035A8]" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={selBase} data-testid="sub-filter-status">
            <option value="">All statuses</option><option value="subscribed">Subscribed</option><option value="unsubscribed">Unsubscribed</option>
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={selBase} data-testid="sub-filter-category">
            <option value="">All categories</option>
            {(stats?.categories || []).map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
          <span className="ml-auto text-sm text-slate-500">{total.toLocaleString()} contacts</span>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
            <div className="col-span-3">Email</div><div className="col-span-2">Name</div>
            <div className="col-span-3">Categories</div><div className="col-span-2">Source</div>
            <div className="col-span-1">Status</div><div className="col-span-1 text-right">Added</div>
          </div>
          {items === null ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-slate-400"><Users className="h-8 w-8" /><p className="text-sm">No subscribers match these filters.</p></div>
          ) : items.map((it) => (
            <div key={it.id} data-testid={`sub-row-${it.email}`} className="grid grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3 md:grid-cols-12 md:items-center md:gap-3">
              <div className="col-span-3 truncate text-sm font-semibold text-slate-800">{it.email}</div>
              <div className="col-span-2 truncate text-sm text-slate-600">{it.name || '—'}</div>
              <div className="col-span-3 flex flex-wrap gap-1">
                {(it.categories || []).length === 0 ? <span className="text-xs text-slate-400">—</span> :
                  it.categories.map((c) => <span key={c} className="rounded-full bg-[#0035A8]/10 px-2 py-0.5 text-xs font-medium text-[#0035A8]">{catMap[c] || c}</span>)}
              </div>
              <div className="col-span-2 text-sm text-slate-500">{it.source || 'website'}</div>
              <div className="col-span-1">
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${it.status === 'subscribed' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{it.status === 'subscribed' ? 'Active' : 'Unsub'}</span>
              </div>
              <div className="col-span-1 text-right text-xs text-slate-400">{fmt(it.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
